import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import { TrendChart } from "../components/charts";
import {
  Badge,
  Banner,
  Card,
  Pager,
  PanelState,
  RangeFilter,
  StatTile,
} from "../components/ui";
import { dateTime, num, titleCase, usd } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { PageMeta, RangeInfo, UserRef } from "../lib/types";

interface UsageResponse {
  range: RangeInfo;
  totals: {
    calls: number;
    failedCalls: number;
    inputTextTokens: number;
    inputImageTokens: number;
    outputTextTokens: number;
    outputImageTokens: number;
    thoughtTokens: number;
    totalTokens: number;
    costUsd: number;
    failedCostUsd: number;
    unpricedCalls: number;
  };
  byJob: Array<{
    job: string;
    calls: number;
    totalTokens: number;
    outputImageTokens: number;
    costUsd: number;
  }>;
  byModel: Array<{
    provider: string;
    model: string;
    calls: number;
    totalTokens: number;
    costUsd: number;
    unpricedCalls: number;
  }>;
  series: Array<{
    day: string;
    calls: number;
    totalTokens: number;
    outputImageTokens: number;
    costUsd: number;
  }>;
  topUsers: Array<{
    userId: string | null;
    calls: number;
    totalTokens: number;
    costUsd: number;
    user: UserRef | null;
  }>;
  calls: Array<{
    id: string;
    job: string;
    provider: string;
    model: string;
    status: "ok" | "no_image" | "error";
    inputTextTokens: number;
    inputImageTokens: number;
    outputTextTokens: number;
    outputImageTokens: number;
    thoughtTokens: number;
    totalTokens: number;
    costUsd: number | null;
    latencyMs: number | null;
    error: string | null;
    createdAt: string;
    userId: string | null;
    projectId: string | null;
    renderId: string | null;
    user: UserRef | null;
  }>;
  pagination: PageMeta;
}

/**
 * ── AI spend ───────────────────────────────────────────────────────────────
 * Every call the API made to an image model, what it consumed, and an estimate
 * of what it cost.
 *
 * The estimate is labelled as one on purpose. Tokens are exact — they are what
 * the provider reported on each response. Cost is tokens × the rates in force
 * when each row was written, and will differ from the invoice by rounding and
 * any tier discount. What this page guarantees is the reverse direction: when
 * the invoice looks wrong, every call behind it can be counted here.
 */
export function UsagePage() {
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);

  const { data, error, loading, stale } = useApi<UsageResponse>(
    "/api/admin/analytics/ai-usage",
    { days, page, pageSize: 25 },
  );

  const t = data?.totals;

  return (
    <Page
      title="AI spend"
      subtitle="Image-model calls, tokens, and estimated cost"
      actions={
        <RangeFilter
          days={days}
          onChange={(d) => {
            setDays(d);
            setPage(1);
          }}
        />
      }
    >
      <div className={stale ? "stale" : undefined}>
        <PanelState loading={loading} error={error}>
          {data && t && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {t.unpricedCalls > 0 && (
                <Banner tone="warning" title="Some calls have no price">
                  {num(t.unpricedCalls)} call
                  {t.unpricedCalls === 1 ? " uses a model" : "s use models"}{" "}
                  with no known rates, so their cost is left out of the totals
                  below rather than guessed. Their tokens are still counted. Add
                  rates with AI_PRICING on the API — see .env.example.
                </Banner>
              )}

              <div className="grid grid-kpi">
                <StatTile
                  label="Estimated cost"
                  value={usd(t.costUsd)}
                  hint={`over ${days} days`}
                />
                <StatTile
                  label="Model calls"
                  value={t.calls}
                  hint={
                    t.calls > 0
                      ? `${usd(t.costUsd / Math.max(1, t.calls - t.unpricedCalls))} per call`
                      : undefined
                  }
                />
                <StatTile
                  label="Total tokens"
                  value={t.totalTokens}
                  hint={`${num(t.outputImageTokens)} output image`}
                />
                <StatTile
                  label="Billed, nothing usable"
                  value={usd(t.failedCostUsd)}
                  hint={`${num(t.failedCalls)} failed call${t.failedCalls === 1 ? "" : "s"}`}
                />
              </div>

              <Card
                title="Tokens per day"
                note="Output image tokens sit alongside the total because on Gemini's image model they are priced twenty times higher than text — they are where the money goes."
              >
                <TrendChart
                  data={data.series}
                  series={[
                    { key: "totalTokens", label: "All tokens" },
                    { key: "outputImageTokens", label: "Output image tokens" },
                  ]}
                />
              </Card>

              <div className="grid grid-2">
                <Card
                  title="By price class"
                  note="How the tokens split by what they are priced as. Output text includes thinking, which is billed at the text rate."
                >
                  <div className="table-wrap">
                    <table>
                      <tbody>
                        <tr>
                          <td>Input — text</td>
                          <td className="num">{num(t.inputTextTokens)}</td>
                        </tr>
                        <tr>
                          <td>Input — images</td>
                          <td className="num">{num(t.inputImageTokens)}</td>
                        </tr>
                        <tr>
                          <td>Output — text &amp; thinking</td>
                          <td className="num">{num(t.outputTextTokens)}</td>
                        </tr>
                        <tr>
                          <td className="cell-strong">Output — images</td>
                          <td className="num cell-strong">
                            {num(t.outputImageTokens)}
                          </td>
                        </tr>
                        <tr>
                          <td className="cell-mute">
                            of which reported as thinking
                          </td>
                          <td className="num cell-mute">
                            {num(t.thoughtTokens)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card title="By job" note="Masks run once per room; renders run every time someone presses Generate.">
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.byJob.length === 0}
                    emptyText="No model calls in this period."
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Job</th>
                            <th className="num">Calls</th>
                            <th className="num">Tokens</th>
                            <th className="num">Est. cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.byJob.map((j) => (
                            <tr key={j.job}>
                              <td className="cell-strong">{titleCase(j.job)}</td>
                              <td className="num">{num(j.calls)}</td>
                              <td className="num">{num(j.totalTokens)}</td>
                              <td className="num">{usd(j.costUsd)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </PanelState>
                </Card>

                <Card title="By model">
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.byModel.length === 0}
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Model</th>
                            <th className="num">Calls</th>
                            <th className="num">Tokens</th>
                            <th className="num">Est. cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.byModel.map((m) => (
                            <tr key={`${m.provider}:${m.model}`}>
                              <td>
                                <span className="mono cell-strong">{m.model}</span>
                                <div className="cell-mute">{m.provider}</div>
                              </td>
                              <td className="num">{num(m.calls)}</td>
                              <td className="num">{num(m.totalTokens)}</td>
                              <td className="num">
                                {m.unpricedCalls > 0 ? (
                                  <Badge tone="warning">no rates</Badge>
                                ) : (
                                  usd(m.costUsd)
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </PanelState>
                </Card>

                <Card title="Heaviest customers" note="Ranked by tokens consumed.">
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.topUsers.length === 0}
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th className="num">Calls</th>
                            <th className="num">Tokens</th>
                            <th className="num">Est. cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.topUsers.map((u) => (
                            <tr key={u.userId ?? "none"}>
                              <td>
                                {u.userId ? (
                                  <Link
                                    to={`/customers/${u.userId}`}
                                    className="cell-strong"
                                    style={{ textDecoration: "underline" }}
                                  >
                                    {u.user?.fullName ?? "Unnamed"}
                                  </Link>
                                ) : (
                                  <span className="cell-mute">Deleted account</span>
                                )}
                              </td>
                              <td className="num">{num(u.calls)}</td>
                              <td className="num">{num(u.totalTokens)}</td>
                              <td className="num">{usd(u.costUsd)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </PanelState>
                </Card>
              </div>

              <Card
                title="Every call"
                note="Newest first. Failed calls are listed too — a model that answers but returns no usable image is still billed."
              >
                <PanelState
                  loading={false}
                  error={null}
                  empty={data.calls.length === 0}
                  emptyText="No model calls in this period."
                >
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Job</th>
                          <th>Status</th>
                          <th>Customer</th>
                          <th className="num">In</th>
                          <th className="num">Out text</th>
                          <th className="num">Out image</th>
                          <th className="num">Total</th>
                          <th className="num">Est. cost</th>
                          <th className="num">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.calls.map((c) => (
                          <tr key={c.id} title={c.error ?? undefined}>
                            <td className="cell-mute" style={{ whiteSpace: "nowrap" }}>
                              {dateTime(c.createdAt)}
                            </td>
                            <td>{titleCase(c.job)}</td>
                            <td>
                              {c.status === "ok" ? (
                                <Badge tone="good">OK</Badge>
                              ) : c.status === "no_image" ? (
                                <Badge tone="warning">No image</Badge>
                              ) : (
                                <Badge tone="critical">Error</Badge>
                              )}
                            </td>
                            <td>
                              {c.userId ? (
                                <Link
                                  to={`/customers/${c.userId}`}
                                  style={{ textDecoration: "underline" }}
                                >
                                  {c.user?.fullName ?? "Unnamed"}
                                </Link>
                              ) : (
                                <span className="cell-mute">—</span>
                              )}
                            </td>
                            <td className="num">
                              {num(c.inputTextTokens + c.inputImageTokens)}
                            </td>
                            <td className="num">{num(c.outputTextTokens)}</td>
                            <td className="num">{num(c.outputImageTokens)}</td>
                            <td className="num cell-strong">{num(c.totalTokens)}</td>
                            <td className="num">{usd(c.costUsd)}</td>
                            <td className="num cell-mute">
                              {c.latencyMs === null
                                ? "—"
                                : `${(c.latencyMs / 1000).toFixed(1)}s`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    meta={data.pagination}
                    onChange={setPage}
                    busy={stale}
                    label="calls"
                  />
                </PanelState>
              </Card>
            </div>
          )}
        </PanelState>
      </div>
    </Page>
  );
}
